<div class="relative mt-8 flex flex-auto flex-col items-center gap-6">
  <div class="flex w-full max-w-3xl flex-col gap-4">
    @if (!attempts() || attempts().length === 0) {
      <div
        class="surface-default flex flex-col items-center justify-center gap-2 rounded-xl border border-neutral-5 p-12 text-center text-neutral-10"
      >
        <mat-icon class="text-3xl text-neutral-8">history</mat-icon>
        <span class="text-base font-medium">Aucune tentative d'envoi enregistrée</span>
      </div>
    } @else {
      @for (attempt of attempts(); track $index; let idx = $index) {
        <div
          class="surface-default flex flex-col gap-4 rounded-xl border border-neutral-5 p-5 shadow-xs transition-all"
        >
          <!-- Card Header -->
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-2.5">
              <span class="rounded-md bg-neutral-a3 px-2 py-0.5 text-xs font-semibold text-neutral-11">
                #{{ attempts().length - idx }}
              </span>

              <!-- Status badge -->
              <div
                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                [class.bg-green-3]="attempt.status === smsAttemptStatus.SUCCESS"
                [class.text-green-11]="attempt.status === smsAttemptStatus.SUCCESS"
                [class.border]="attempt.status === smsAttemptStatus.SUCCESS"
                [class.border-green-6]="attempt.status === smsAttemptStatus.SUCCESS"
                [class.bg-red-3]="attempt.status === smsAttemptStatus.FAILED"
                [class.text-red-11]="attempt.status === smsAttemptStatus.FAILED"
                [class.border]="attempt.status === smsAttemptStatus.FAILED"
                [class.border-red-6]="attempt.status === smsAttemptStatus.FAILED"
                [class.bg-amber-3]="attempt.status === smsAttemptStatus.PENDING"
                [class.text-amber-11]="attempt.status === smsAttemptStatus.PENDING"
                [class.border]="attempt.status === smsAttemptStatus.PENDING"
                [class.border-amber-6]="attempt.status === smsAttemptStatus.PENDING"
                [class.bg-neutral-3]="attempt.status === smsAttemptStatus.CANCELED"
                [class.text-neutral-11]="attempt.status === smsAttemptStatus.CANCELED"
                [class.border]="attempt.status === smsAttemptStatus.CANCELED"
                [class.border-neutral-6]="attempt.status === smsAttemptStatus.CANCELED"
              >
                <div
                  class="size-1.5 rounded-full"
                  [class.bg-green-9]="attempt.status === smsAttemptStatus.SUCCESS"
                  [class.bg-red-9]="attempt.status === smsAttemptStatus.FAILED"
                  [class.bg-amber-9]="attempt.status === smsAttemptStatus.PENDING"
                  [class.bg-neutral-9]="attempt.status === smsAttemptStatus.CANCELED"
                ></div>
                {{ attempt.status | smsAttemptStatusDisplay }}
              </div>

              <!-- Trigger badge -->
              <div
                class="inline-flex items-center gap-1 rounded-md bg-neutral-a3 px-2 py-0.5 text-xs font-medium text-neutral-11"
              >
                <mat-icon class="text-xs! size-3.5! leading-3.5!">
                  {{
                    attempt.triggered === smsTriggers.DIRECT
                      ? 'bolt'
                      : attempt.triggered === smsTriggers.MANUAL
                        ? 'touch_app'
                        : 'schedule'
                  }}
                </mat-icon>
                <span>{{ attempt.triggered | smsTriggerDisplay }}</span>
              </div>
            </div>

            <!-- Date -->
            <div
              class="flex items-center gap-1.5 text-xs text-neutral-10"
              [matTooltip]="attempt.date | luxonDate: 'humanize'"
            >
              <mat-icon class="text-xs! size-3.5! leading-3.5!">access_time</mat-icon>
              <span>{{ attempt.date | luxonDate: 'dd/LL/yyyy HH:mm:ss' }}</span>
            </div>
          </div>

          <!-- Failure reason if failed -->
          @if (attempt.failureReason) {
            <div
              class="flex items-start gap-2.5 rounded-lg border border-red-6 bg-red-2 p-3 text-sm text-red-11"
            >
              <mat-icon class="text-sm! size-4! shrink-0 text-red-9">error_outline</mat-icon>
              <span class="break-all font-mono text-xs leading-relaxed">{{ attempt.failureReason }}</span>
            </div>
          }

          <!-- Gateway Events Timeline -->
          @let events = getGatewayEvents(attempt);
          @if (events.length > 0) {
            <div class="flex flex-col gap-2 rounded-lg border border-neutral-4 bg-neutral-2/50 p-3.5">
              <div class="flex items-center gap-1.5 text-xs font-semibold text-neutral-11">
                <mat-icon class="text-xs! size-3.5! leading-3.5! text-neutral-9">hub</mat-icon>
                <span>Suivi des événements de la passerelle</span>
              </div>

              <div class="flex flex-col gap-2 pt-1">
                @for (ev of events; track $index) {
                  @let color = getEventColorClass(ev);
                  <div class="flex items-start justify-between gap-3 text-xs">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium"
                        [class]="color.bg + ' ' + color.text"
                      >
                        <mat-icon class="text-xs! size-3! leading-3!">{{ getEventIcon(ev) }}</mat-icon>
                        <span>{{ getEventTitle(ev) }}</span>
                      </span>

                      @if (getEventRecipient(ev); as recipient) {
                        <span class="text-neutral-10 font-mono text-[11px]">{{ recipient }}</span>
                      }

                      @if (getEventParts(ev); as parts) {
                        <span class="rounded bg-neutral-a3 px-1.5 py-0.2 text-[10px] text-neutral-10">
                          {{ parts }} {{ parts > 1 ? 'segments' : 'segment' }}
                        </span>
                      }

                      @if (getEventReason(ev); as reason) {
                        <span class="text-red-11 font-mono text-[11px]">{{ reason }}</span>
                      }
                    </div>

                    @if (getEventDate(ev); as evDate) {
                      <span class="text-neutral-10 font-mono text-[11px] shrink-0">
                        {{ evDate | luxonDate: 'HH:mm:ss' }}
                      </span>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- Gateway response / Raw Metadata -->
          @if (attempt.metadata) {
            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-1.5 text-xs font-semibold text-neutral-11">
                <mat-icon class="text-xs! size-3.5! leading-3.5! text-neutral-9">terminal</mat-icon>
                <span>
                  {{
                    attempt.metadata['gatewayResponse']
                      ? 'Réponse de la passerelle SMS (gatewayResponse)'
                      : 'Métadonnées'
                  }}
                </span>
              </div>

              <aura-code-block [code]="attempt.metadata['gatewayResponse'] ?? attempt.metadata" />
            </div>
          }
        </div>
      }
    }
  </div>
</div>
